// Project lifecycle and estimating types.
// Listed in chronological order — see ORDERED_STAGES in
// utils/stageUtils.ts for the canonical display sequence and the full
// domain note on "client" vs "Employer".
//
// Stage IDs use QS-consultant terminology (ITT, Internal Review,
// Cleared for Submission, Pre-Start, Defects Liability) rather than
// the older generic names. `normalizeProjectStage` accepts the old
// IDs as aliases so any stale values still display correctly during
// the transition window.
export type ProjectStage =
  | 'enquiry_received'
  | 'tender_in_progress'
  | 'client_review'
  | 'ready_to_submit'
  | 'submitted'
  | 'pre_contract'
  | 'itt_received'
  | 'tender_pending_review'
  | 'tendering'
  | 'internal_review'
  | 'cleared_for_submission'
  | 'tender_submitted'
  | 'on_hold'
  | 'pre_start'
  | 'on_site'
  | 'practical_completion'
  | 'final_account'
  | 'defects_liability'
  | 'retention_period'
  | 'closed';

export type ProjectPriority = 'low' | 'medium' | 'high' | 'urgent';

export type EstimateStatus = 'not_started' | 'in_progress' | 'review' | 'completed' | 'submitted';

export interface ProjectInquiry {
  id: string;
  title: string;
  client_company_id: string;
  client_name: string;
  contact_person: string;
  contact_email: string;
  contact_phone?: string;
  
  // Project Details
  project_type: string;
  project_location: string;
  estimated_value?: number;
  tender_deadline?: string;
  project_start_date?: string;
  project_duration_weeks?: number;
  
  // Employer/Main Contractor Details
  employer_id?: string;
  employer_name?: string;
  main_contractor?: string;
  contract_type?: 'JCT' | 'NEC3' | 'NEC4' | 'Bespoke';
  
  // Tender Risk Assessment
  tender_risks?: string[];
  scope_risks?: string[];
  contract_risk_level?: 'low' | 'medium' | 'high' | 'critical';
  
  // Tender Assumptions & Basis of Estimate
  pricing_assumptions?: string[];
  basis_of_estimate?: string;
  exclusions?: string[];
  
  // Preliminaries
  prelims_estimate?: number;
  prelims_actual?: number;
  
  // Service Requirements
  services_required: string[];
  
  // Lifecycle Tracking
  stage: ProjectStage;
  priority: ProjectPriority;
  assigned_estimator?: string;
  assigned_qs?: string;
  
  // Dates
  inquiry_date: string;
  quote_due_date?: string;
  quote_submitted_date?: string;
  decision_deadline?: string;
  
  // Commercial
  quote_value?: number;
  quote_margin?: number;
  win_probability?: number;
  
  // Status
  status: 'active' | 'won' | 'lost' | 'on_hold';
  created_at: string;
  updated_at: string;
  
  // Notes and attachments
  notes?: string;
  attachments?: string[];
  tender_documents?: string[];
}

export interface Estimate {
  id: string;
  project_inquiry_id: string;
  title: string;
  
  // Estimate Details
  estimate_status: EstimateStatus;
  total_value: number;
  breakdown: EstimateBreakdown[];
  
  // Resources
  estimated_hours: number;
  hourly_rate: number;
  expenses: number;
  margin_percentage: number;
  
  // Team
  lead_estimator: string;
  supporting_team: string[];
  
  // Timeline
  started_date?: string;
  target_completion_date: string;
  completed_date?: string;
  submitted_date?: string;
  
  // Review
  review_status: 'pending' | 'in_review' | 'approved' | 'rejected';
  reviewer?: string;
  review_notes?: string;
  
  created_at: string;
  updated_at: string;
}

export interface EstimateBreakdown {
  id: string;
  category: string;
  description: string;
  quantity: number;
  unit: string;
  unit_rate: number;
  total: number;
  notes?: string;
}

export interface ProjectMilestone {
  id: string;
  project_id: string;
  title: string;
  description: string;
  stage: ProjectStage;
  due_date: string;
  completed_date?: string;
  assigned_to: string;
  status: 'pending' | 'in_progress' | 'completed' | 'overdue';
  dependencies?: string[];
}

// Interim Application Reference (lightweight for project views)
export interface InterimApplicationRef {
  application_no: number;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'paid';
  amount_due: number;
  period_ending: Date;
  app_date: Date;
  submitted_date?: Date;
  approved_date?: Date;
  paid_date?: Date;
}

// Stage display information
export const STAGE_INFO: Record<ProjectStage, { label: string; color: string; icon: string }> = {
  enquiry_received:       { label: 'Enquiry Received',        color: 'sky',    icon: 'Inbox' },
  tender_in_progress:     { label: 'Tender in Progress',      color: 'orange', icon: 'Play' },
  client_review:          { label: 'Client Review',           color: 'yellow', icon: 'Eye' },
  ready_to_submit:        { label: 'Ready to Submit',         color: 'purple', icon: 'FileCheck' },
  submitted:              { label: 'Submitted',               color: 'green',  icon: 'Send' },
  pre_contract:           { label: 'Pre-Contract',            color: 'indigo', icon: 'FileSignature' },
  itt_received:           { label: 'Tender Received',         color: 'blue',   icon: 'Mail' },
  tender_pending_review:  { label: 'QC Check',                color: 'blue',   icon: 'Clock' },
  tendering:              { label: 'In Progress',             color: 'orange', icon: 'Play' },
  internal_review:        { label: 'Client Review',           color: 'yellow', icon: 'UserCheck' },
  cleared_for_submission: { label: 'Cleared for Submission',  color: 'purple', icon: 'FileEdit' },
  tender_submitted:       { label: 'Tender Submitted',        color: 'green',  icon: 'Send' },
  on_hold:                { label: 'On Hold',                 color: 'gray',   icon: 'Pause' },
  pre_start:              { label: 'Pre-Start',               color: 'indigo', icon: 'Handshake' },
  on_site:                { label: 'On Site',                 color: 'blue',   icon: 'Hammer' },
  practical_completion:   { label: 'Practical Completion',    color: 'cyan',   icon: 'Award' },
  final_account:          { label: 'Final Account',           color: 'amber',  icon: 'Receipt' },
  defects_liability:      { label: 'Defects Liability (DLP)', color: 'rose',   icon: 'Clock' },
  retention_period:       { label: 'Retention Period',        color: 'orange', icon: 'Timer' },
  closed:                 { label: 'Closed',                  color: 'green',  icon: 'CheckCircle2' }
};